import { useEffect, useMemo, useState } from 'react';
import { Search, Loader2, LayoutTemplate, X, Check } from 'lucide-react';
import { templateApi } from '../../services/api';
import { useReportStore } from '../../stores/reportStore';
import { templateToHtml } from '../../lib/templateToHtml';
import { MODALITIES } from '../../types';
import type { ReportTemplate } from '../../types';

interface Section {
  name: string;
  normal_default?: string;
  fixed_prefix?: string;
}

/**
 * Persistent docked template panel (RAAS reporter right column).
 *
 * We are AI-first: picking a template here sets the manual override the
 * engine uses as a generation scaffold — it is NOT a fill-in form. Selection
 * is stored in reportStore.selectedTemplateId, shared with the dictation panel.
 */
export default function TemplateRail({ onClose }: { onClose: () => void }) {
  const { selectedTemplateId, setSelectedTemplate, setGeneratedReport, editedReport, generatedReport } =
    useReportStore();

  // Selecting a template drops its canonical normals into the editor as the
  // starting point for the flow (reduces blank-page / missed-section errors).
  const selectTemplate = (t: ReportTemplate) => {
    const isSel = t.id === selectedTemplateId;
    if (isSel) {
      setSelectedTemplate('', '');
      return;
    }
    const html = templateToHtml(t);
    // Guard: setGeneratedReport overwrites the edited report AND wipes the
    // undo history. If the radiologist has unsaved edits, confirm before
    // destroying them (this is a click handler, so confirm() is reliable).
    const hasUnsavedWork = editedReport.trim() !== '' && editedReport !== generatedReport;
    if (html && hasUnsavedWork &&
        !window.confirm(`Replace the current report with "${t.name}"? Your edits and undo history will be lost.`)) {
      return;
    }
    setSelectedTemplate(t.id, t.name);
    if (html) setGeneratedReport(html);
  };
  const [tab, setTab] = useState<'templates' | 'macros'>('templates');
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalityFilter, setModalityFilter] = useState<string>('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params: { modality?: string } = {};
        if (modalityFilter !== 'all') params.modality = modalityFilter;
        const result = await templateApi.list(params);
        if (!cancelled) setTemplates(result);
      } catch {
        if (!cancelled) setError('Failed to load templates');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modalityFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.exam_category || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q)
    );
  }, [templates, query]);

  const selected = templates.find((t) => t.id === selectedTemplateId);
  const previewSections: Section[] =
    ((selected?.template_config as { sections?: Section[] } | undefined)?.sections) || [];

  return (
    <aside className="w-72 bg-white dark:bg-surface-900 border-l border-gray-200 dark:border-surface-800 flex flex-col shrink-0">
      {/* Tab header */}
      <div className="flex items-center border-b border-gray-200 dark:border-surface-800 text-sm">
        <button
          onClick={() => setTab('templates')}
          className={`flex-1 py-2.5 font-medium transition-colors ${
            tab === 'templates'
              ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500'
              : 'text-gray-400 dark:text-gray-500 hover:text-gray-600'
          }`}
        >
          Templates
        </button>
        <button
          onClick={() => setTab('macros')}
          className={`flex-1 py-2.5 font-medium transition-colors ${
            tab === 'macros'
              ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-500'
              : 'text-gray-400 dark:text-gray-500 hover:text-gray-600'
          }`}
        >
          Macros
        </button>
        <button
          onClick={onClose}
          title="Hide panel"
          className="px-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          <X size={16} />
        </button>
      </div>

      {tab === 'macros' ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-6 text-gray-400 dark:text-gray-500">
          <LayoutTemplate size={32} className="mb-3 opacity-50" />
          <p className="text-sm">Macros arrive in Phase 4 (voice-triggered snippets).</p>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="p-2 space-y-2 border-b border-gray-200 dark:border-surface-800">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search templates…"
                className="w-full pl-8 pr-2 py-1.5 bg-gray-50 dark:bg-surface-850 border border-gray-200 dark:border-surface-800 rounded-md text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <select
              value={modalityFilter}
              onChange={(e) => setModalityFilter(e.target.value)}
              className="w-full px-2 py-1.5 bg-gray-50 dark:bg-surface-850 border border-gray-200 dark:border-surface-800 rounded-md text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="all">All modalities</option>
              {MODALITIES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 size={20} className="animate-spin text-primary-500" />
              </div>
            ) : error ? (
              <div className="m-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded text-red-700 dark:text-red-400 text-xs">
                {error}
              </div>
            ) : filtered.length === 0 ? (
              <p className="p-4 text-xs text-gray-400 text-center">No templates found.</p>
            ) : (
              filtered.map((t) => {
                const isSel = t.id === selectedTemplateId;
                return (
                  <button
                    key={t.id}
                    onClick={() => selectTemplate(t)}
                    className={`w-full text-left px-3 py-2 border-b border-gray-100 dark:border-surface-800/60 transition-colors ${
                      isSel
                        ? 'bg-primary-50 dark:bg-primary-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-surface-850'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      {isSel && <Check size={13} className="text-primary-600 dark:text-primary-400 shrink-0" />}
                      <span className="text-sm text-gray-900 dark:text-white truncate">{t.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">
                        {(t.exam_category || t.modality || '').replace(/_/g, ' ')}
                      </span>
                      {t.source === 'raas' && (
                        <span className="text-[10px] px-1 rounded bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-300">
                          RAAS
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Selection / preview footer */}
          <div className="border-t border-gray-200 dark:border-surface-800 p-2 text-xs">
            {selected ? (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-700 dark:text-gray-200 truncate">{selected.name}</span>
                  <button
                    onClick={() => setSelectedTemplate('', '')}
                    className="text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    Clear
                  </button>
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {previewSections.map((s, i) => (
                    <div key={i}>
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
                        {s.name}
                      </span>
                      {(s.normal_default || s.fixed_prefix) && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2">
                          {s.normal_default || s.fixed_prefix}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-400 dark:text-gray-500">
                Auto-detect — the classifier picks a template. Select one to override.
              </p>
            )}
          </div>
        </>
      )}
    </aside>
  );
}
